import { clampEditorGridColumns } from './geometry.js';

export type WebEditorAutoLayoutFlow = 'horizontal' | 'vertical' | 'wrap' | 'grid';
export type WebEditorSizingMode = 'hug' | 'fill' | 'fixed';
export type WebEditorAutoLayoutAlignment = 'start' | 'center' | 'end';

export interface WebEditorAutoLayoutFrame {
  flow: WebEditorAutoLayoutFlow;
  gapX: number;
  gapY: number;
  paddingX: number;
  paddingY: number;
  alignX: WebEditorAutoLayoutAlignment;
  alignY: WebEditorAutoLayoutAlignment;
  widthMode: WebEditorSizingMode;
  heightMode: WebEditorSizingMode;
  fixedWidth?: number;
  fixedHeight?: number;
  columns?: number;
}

export interface WebEditorAutoLayoutItem {
  order: number;
  widthMode: WebEditorSizingMode;
  heightMode: WebEditorSizingMode;
  fixedWidth?: number;
  fixedHeight?: number;
  columnSpan?: number;
  rowSpan?: number;
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;
const finite = (value: unknown) => (typeof value === 'number' && Number.isFinite(value) ? value : undefined);
const clampSpace = (value: number) => Math.min(320, Math.max(0, Math.round(value)));
export const clampAutoLayoutSize = (value: number) => Math.min(2400, Math.max(24, Math.round(value)));
const isFlow = (value: unknown): value is WebEditorAutoLayoutFlow =>
  value === 'horizontal' || value === 'vertical' || value === 'wrap' || value === 'grid';
const isSizing = (value: unknown): value is WebEditorSizingMode =>
  value === 'hug' || value === 'fill' || value === 'fixed';
const isAlignment = (value: unknown): value is WebEditorAutoLayoutAlignment =>
  value === 'start' || value === 'center' || value === 'end';

export const defaultAutoLayoutFrame = (flow: WebEditorAutoLayoutFlow = 'vertical'): WebEditorAutoLayoutFrame => ({
  flow,
  gapX: 16,
  gapY: 16,
  paddingX: 0,
  paddingY: 0,
  alignX: 'start',
  alignY: 'start',
  widthMode: 'fill',
  heightMode: 'hug',
  ...(flow === 'grid' ? { columns: 12 } : {}),
});

export const defaultAutoLayoutItem = (order = 0): WebEditorAutoLayoutItem => ({
  order,
  widthMode: 'hug',
  heightMode: 'hug',
});

export const normalizeAutoLayoutFrame = (value: unknown): WebEditorAutoLayoutFrame | undefined => {
  if (!isRecord(value) || !isFlow(value.flow)) return undefined;
  const fallback = defaultAutoLayoutFrame(value.flow);
  const widthMode = isSizing(value.widthMode) ? value.widthMode : fallback.widthMode;
  const heightMode = isSizing(value.heightMode) ? value.heightMode : fallback.heightMode;
  const fixedWidth = finite(value.fixedWidth);
  const fixedHeight = finite(value.fixedHeight);
  const columns = finite(value.columns);
  return {
    flow: value.flow,
    gapX: clampSpace(finite(value.gapX) ?? fallback.gapX),
    gapY: clampSpace(finite(value.gapY) ?? fallback.gapY),
    paddingX: clampSpace(finite(value.paddingX) ?? fallback.paddingX),
    paddingY: clampSpace(finite(value.paddingY) ?? fallback.paddingY),
    alignX: isAlignment(value.alignX) ? value.alignX : fallback.alignX,
    alignY: isAlignment(value.alignY) ? value.alignY : fallback.alignY,
    widthMode,
    heightMode,
    ...(widthMode === 'fixed' && fixedWidth !== undefined ? { fixedWidth: clampAutoLayoutSize(fixedWidth) } : {}),
    ...(heightMode === 'fixed' && fixedHeight !== undefined ? { fixedHeight: clampAutoLayoutSize(fixedHeight) } : {}),
    ...(value.flow === 'grid' ? { columns: clampEditorGridColumns(columns ?? fallback.columns ?? 12) } : {}),
  };
};

export const normalizeAutoLayoutItem = (value: unknown, columns = 20): WebEditorAutoLayoutItem | undefined => {
  if (!isRecord(value)) return undefined;
  const widthMode = isSizing(value.widthMode) ? value.widthMode : 'hug';
  const heightMode = isSizing(value.heightMode) ? value.heightMode : 'hug';
  const fixedWidth = finite(value.fixedWidth);
  const fixedHeight = finite(value.fixedHeight);
  const columnSpan = finite(value.columnSpan);
  const rowSpan = finite(value.rowSpan);
  return {
    order: Math.max(0, Math.round(finite(value.order) ?? 0)),
    widthMode,
    heightMode,
    ...(widthMode === 'fixed' && fixedWidth !== undefined ? { fixedWidth: clampAutoLayoutSize(fixedWidth) } : {}),
    ...(heightMode === 'fixed' && fixedHeight !== undefined ? { fixedHeight: clampAutoLayoutSize(fixedHeight) } : {}),
    ...(columnSpan !== undefined ? { columnSpan: Math.min(clampEditorGridColumns(columns), Math.max(1, Math.round(columnSpan))) } : {}),
    ...(rowSpan !== undefined ? { rowSpan: Math.min(20, Math.max(1, Math.round(rowSpan))) } : {}),
  };
};

export const autoLayoutItemsFitRow = (containerWidth: number, itemWidths: readonly number[], gap: number, paddingX: number) =>
  itemWidths.reduce((sum, width) => sum + Math.max(0, width), 0) + Math.max(0, itemWidths.length - 1) * Math.max(0, gap) <=
  Math.max(0, containerWidth - Math.max(0, paddingX) * 2);

